import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  TextField,
  Typography,
  Button,
  Box,
  Avatar,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useSections } from "./SectionContext";

// Map section name → route
const sectionRoutes = {
  "Photo, Personal Details": "/PersonalDetail",
  "Websites, Portfolios, Profiles": "/WebsiteSection",
  Certifications: "/Certifications",
  Languages: "/Language",
  Accomplishments: "/AccomplishmentsSection",
  "Additional Information": "/AdditionalInfoForm",
  Software: "/SoftWare",
  References: "/Refferences",
  Volunteering: "/Volunteer",
  Interests: "/Interest",
};

// Templates that do NOT support a photo
const NO_IMAGE_TEMPLATES = [6,8,12,18,19,20,21,27,28,29,30,31,32,33,35,43,44,45,46,49];

const PersonalDetails = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    city: "",
    country: "India",
    pinCode: "",
    phone: "",
    email: "",
  });

  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState("");

  const { sections, currentIndex, goNext } = useSections();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedTemplate, setSelectedTemplate] = useState(1);
  const [selectedTheme, setSelectedTheme] = useState("#1a2b47");

  useEffect(() => {
    if (location.state) {
      const { selectedTemplate: template, selectedTheme: theme } = location.state;
      if (template) setSelectedTemplate(template);
      if (theme) setSelectedTheme(theme);
    }
  }, [location.state]);

  // Derive whether this template supports photo
  const isPhotoTemplate = !NO_IMAGE_TEMPLATES.includes(selectedTemplate);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const res = await axios.get("https://resume-maker-lc.onrender.com/api/contact/me", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data) {
          setFormData({
            firstName: res.data.firstName || "",
            lastName: res.data.lastName || "",
            city: res.data.city || "",
            country: res.data.country || "India",
            pinCode: res.data.pinCode || "",
            phone: res.data.phone || "",
            email: res.data.email || "",
          });

          if (res.data.photo) {
            setPreview(
              res.data.photo.startsWith("http")
                ? res.data.photo
                : `https://resume-maker-lc.onrender.com${res.data.photo}`
            );
          }
        }
      } catch (err) {
        console.error("Error fetching details", err);
        if (err.response?.status === 403) {
          localStorage.removeItem("token");
          alert("Session expired. Please login again.");
          navigate("/login");
        }
      }
    };

    fetchData();
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    // Only allow if template supports photo
    if (!isPhotoTemplate) return;
    const file = e.target.files[0];
    setPhoto(file);
    if (file) setPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    try {
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
        alert("First name, Last name, Email, and Phone are required!");
        return false;
      }

      const token = localStorage.getItem("token");
      const data = new FormData();
      Object.keys(formData).forEach((key) => data.append(key, formData[key]));

      // Only attach photo if template supports it
      if (isPhotoTemplate && photo) {
        data.append("photo", photo);
      }

      await axios.post("https://resume-maker-lc.onrender.com/api/contact/save", data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Personal details saved successfully!");
      return true;
    } catch (err) {
      console.error(err.response?.data || err.message);
      alert("Error saving personal details");
      return false;
    }
  };

  const handleSaveNext = async () => {
    const saved = await handleSave();
    if (!saved) return;

    goNext();
    const nextSection = sections[currentIndex + 1];
    if (nextSection) {
      navigate(sectionRoutes[nextSection], {
        state: { selectedTemplate, selectedTheme },
      });
    } else {
      navigate("/mainpage", {
        state: { selectedTemplate, selectedTheme },
      });
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", backgroundColor: "#EFF7FF", minHeight: "100vh" }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: "#002B5B" }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: "bold", letterSpacing: 0.5 }}>
            Personal Details
          </Typography>
        </Toolbar>
      </AppBar>

      <Container
        maxWidth="md"
        sx={{
          mt: { xs: 2, md: 4 },
          mb: 4,
          px: { xs: 2, sm: 3, md: 4 },
          mx: "auto",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 3,
            border: "1px solid #B3D9F7",
            boxShadow: "0 4px 20px rgba(0, 43, 91, 0.12)",
            backgroundColor: "#ffffff",
          }}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            gutterBottom
            sx={{ color: "#002B5B", borderBottom: "2px solid #0288D1", pb: 1 }}
          >
            Personal Details
          </Typography>
          <Typography variant="body2" sx={{ color: "#0288D1" }} gutterBottom>
            Update your basic personal information here.
          </Typography>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            {/* Photo section — shown only for photo-supported templates */}
            <Grid item xs={12} sm={3} sx={{ textAlign: "center" }}>
              {isPhotoTemplate ? (
                // WITH PHOTO TEMPLATE: show avatar + upload button
                <>
                  <Avatar
                    src={preview}
                    sx={{
                      width: 100,
                      height: 100,
                      mx: "auto",
                      mb: 2,
                      border: "3px solid #0288D1",
                      boxShadow: "0 2px 10px rgba(2, 136, 209, 0.3)",
                    }}
                  />
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      textTransform: "none",
                      borderColor: "#0288D1",
                      color: "#002B5B",
                      fontWeight: "bold",
                      "&:hover": {
                        backgroundColor: "#E1F5FE",
                        borderColor: "#002B5B",
                      },
                    }}
                  >
                    Upload Photo
                    <input hidden accept="image/*" type="file" onChange={handleFileChange} />
                  </Button>
                </>
              ) : (
                // WITHOUT PHOTO TEMPLATE: show avatar placeholder + info alert
                <>
                  <Avatar
                    sx={{
                      width: 100,
                      height: 100,
                      mx: "auto",
                      mb: 2,
                      bgcolor: "#B3D9F7",
                      color: "#002B5B",
                      fontSize: "2rem",
                    }}
                  >
                    👤
                  </Avatar>
                  <Alert
                    severity="info"
                    sx={{
                      fontSize: "0.75rem",
                      textAlign: "left",
                      py: 0.5,
                      backgroundColor: "#E1F5FE",
                      color: "#002B5B",
                      border: "1px solid #B3D9F7",
                      "& .MuiAlert-icon": { color: "#0288D1" },
                    }}
                  >
                    This template does not support a profile photo. Other details will be saved normally.
                  </Alert>
                </>
              )}
            </Grid>

            {/* Form fields — unchanged */}
            <Grid item xs={12} sm={9}>
              <Grid container spacing={2}>
                {[
                  { label: "First Name", name: "firstName", required: true },
                  { label: "Last Name", name: "lastName", required: true },
                  { label: "City", name: "city" },
                  { label: "Country", name: "country" },
                  { label: "Pin Code", name: "pinCode" },
                  { label: "Phone", name: "phone", required: true },
                  { label: "Email", name: "email", required: true },
                ].map((field) => (
                  <Grid
                    item
                    xs={12}
                    sm={field.name === "country" || field.name === "pinCode" ? 3 : 6}
                    key={field.name}
                  >
                    <TextField
                      fullWidth
                      label={field.label}
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      required={field.required || false}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "#B3D9F7" },
                          "&:hover fieldset": { borderColor: "#0288D1" },
                          "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                        },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#002B5B" },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderColor: "#0288D1",
                color: "#002B5B",
                fontWeight: "bold",
                "&:hover": { backgroundColor: "#E1F5FE", borderColor: "#002B5B" },
              }}
            >
              Back
            </Button>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  backgroundColor: "#0288D1",
                  fontWeight: "bold",
                  "&:hover": { backgroundColor: "#0277BD" },
                  boxShadow: "0 3px 10px rgba(2, 136, 209, 0.3)",
                }}
              >
                Save
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#002B5B",
                  fontWeight: "bold",
                  "&:hover": { backgroundColor: "#0288D1" },
                  boxShadow: "0 3px 10px rgba(0, 43, 91, 0.3)",
                }}
                onClick={handleSaveNext}
              >
                Save & Next
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PersonalDetails;